import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Send, Paperclip, MessageCircle, Check, CheckCheck, ArrowDown } from 'lucide-react';

// Helper: human-readable date label
function getDateLabel(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.setHours(0,0,0,0) - new Date(date).setHours(0,0,0,0)) / 86400000);
  
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return date.toLocaleDateString('es-ES', { weekday: 'long' });
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function CoachAthleteMessaging({ athleteId, athleteName, currentUser, targetUser, messages, onSendMessage, onMarkRead }) {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Filter messages to only show conversation between current user and target user
  const conversation = messages?.filter(m => 
    (m.senderId === currentUser?.id && m.receiverId === targetUser?.id) ||
    (m.senderId === targetUser?.id && m.receiverId === currentUser?.id)
  ) || [];

  // Group messages by date
  const groupedMessages = useMemo(() => {
    const groups = [];
    let currentDate = null;

    conversation.forEach(msg => {
      const msgDate = new Date(msg.createdAt).toLocaleDateString('es-ES', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      });
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ type: 'date', label: getDateLabel(msg.createdAt), key: `date-${msgDate}` });
      }
      groups.push({ type: 'message', data: msg, key: msg.id });
    });
    return groups;
  }, [conversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (conversation.length > 0 && targetUser?.id) {
      const hasUnread = conversation.some(m => m.receiverId === currentUser?.id && !m.isRead);
      if (hasUnread && onMarkRead) {
        onMarkRead(targetUser.id);
      }
    }
    scrollToBottom();
  }, [conversation.length, targetUser?.id]);

  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    setShowScrollBtn(scrollHeight - scrollTop - clientHeight > 100);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !targetUser?.id) return;
    
    setSending(true);
    try {
      await onSendMessage(targetUser.id, newMessage.trim());
      setNewMessage('');
    } catch (err) {
      alert('Error enviando mensaje: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !targetUser?.id) return;
    
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');
      
      const uploadRes = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!uploadRes.ok) throw new Error('Falló la subida del archivo');
      const uploadData = await uploadRes.json();
      
      // Enviar el link en el mensaje
      await onSendMessage(targetUser.id, `📎 Archivo adjunto: [${uploadData.name}](${uploadData.url})`);
    } catch (err) {
      alert('Error enviando archivo: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  // Helper: parse markdown links for files
  const renderMessageContent = (content) => {
    const fileLinkRegex = /\[(.*?)\]\((.*?)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = fileLinkRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }
      const [_, text, url] = match;
      parts.push(
        <a key={match.index} href={url} target="_blank" rel="noopener noreferrer" className="messaging-file-link">
          {text}
        </a>
      );
      lastIndex = fileLinkRegex.lastIndex;
    }
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }
    return <>{parts.length > 0 ? parts : content}</>;
  };

  return (
    <div className="messaging-inline-container">
      {/* Header */}
      <div className="messaging-inline-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div className="messaging-inline-avatar">
            <MessageCircle size={16} />
          </div>
          <h3 className="messaging-inline-title">
            Mensajes con {athleteName?.split(' ')[0] || 'Atleta'}
          </h3>
        </div>
        <span className="messaging-inline-count">
          {conversation.length} {conversation.length === 1 ? 'mensaje' : 'mensajes'}
        </span>
      </div>

      {/* Chat History */}
      <div 
        className="messaging-inline-chat"
        ref={chatContainerRef}
        onScroll={handleScroll}
      >
        <div className="messaging-chat-pattern" />
        
        {groupedMessages.length === 0 ? (
          <div className="messaging-empty-state" style={{ padding: '2rem 1rem' }}>
            <MessageCircle size={36} strokeWidth={1} style={{ color: 'rgba(255,255,255,0.12)', marginBottom: '0.75rem' }} />
            <p>No hay mensajes aún. ¡Inicia la conversación!</p>
          </div>
        ) : (
          groupedMessages.map(item => {
            if (item.type === 'date') {
              return (
                <div key={item.key} className="messaging-date-separator">
                  <span>{item.label}</span>
                </div>
              );
            }
            const msg = item.data;
            const isMine = msg.senderId === currentUser?.id;
            return (
              <div key={item.key} className={`messaging-bubble-wrapper ${isMine ? 'mine' : 'theirs'}`}>
                <div className={`messaging-bubble ${isMine ? 'mine' : 'theirs'}`}>
                  {renderMessageContent(msg.content)}
                  <span className="messaging-bubble-meta">
                    {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {isMine && (
                      msg.isRead 
                        ? <CheckCheck size={14} className="messaging-read-icon read" /> 
                        : <Check size={14} className="messaging-read-icon" />
                    )}
                  </span>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom */}
      {showScrollBtn && (
        <button className="messaging-scroll-btn inline" onClick={scrollToBottom}>
          <ArrowDown size={16} />
        </button>
      )}

      {/* Input Area */}
      <div className="messaging-input-area inline">
        <form onSubmit={handleSend} className="messaging-input-form" style={{ position: 'relative' }}>
          
          <button 
            type="button"
            title="Generar Borrador con IA"
            onClick={async () => {
              if (!targetUser?.id) return;
              try {
                setNewMessage('Generando borrador...');
                
                if (targetUser.id.startsWith('demo-')) {
                  setTimeout(() => {
                    setNewMessage(`Hola ${targetUser.name.split(' ')[0]}, he notado que tu estado marca "${targetUser.recentStatus || 'Fatiga'}" hoy. ¿Te encuentras bien? ¿Prefieres que reduzcamos la intensidad de las series?`);
                  }, 1200);
                  return;
                }

                const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');
                const res = await fetch(`${API_URL}/ai/coach-message-draft?userId=${targetUser.id}`, {
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                const data = await res.json();
                if (data.draft) {
                  setNewMessage(data.draft);
                } else {
                  setNewMessage('');
                }
              } catch (e) {
                console.error(e);
                setNewMessage('');
              }
            }}
            style={{
              position: 'absolute', right: '3.5rem', top: '50%', transform: 'translateY(-50%)',
              background: 'transparent', border: 'none', color: '#a78bfa', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.4rem',
              borderRadius: '50%', transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(167, 139, 250, 0.1)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
          >
            ✨
          </button>

          <label className="messaging-attach-btn">
            <Paperclip size={16} />
            <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
          </label>
          <input 
            type="text" 
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            disabled={!targetUser || sending}
            placeholder="Escribe un mensaje..."
            className="messaging-text-input"
            style={{ paddingRight: '2.5rem' }}
          />
          <button 
            type="submit" 
            disabled={!newMessage.trim() || sending || !targetUser || newMessage === 'Generando borrador...'} 
            className={`messaging-send-btn ${newMessage.trim() && newMessage !== 'Generando borrador...' ? 'active' : ''}`}
          >
            <Send size={16} style={{ marginLeft: '-1px' }} />
          </button>
        </form>
      </div>
    </div>
  );
}
